import { FC, lazy, LazyExoticComponent, Suspense } from "react";
import { Navigate } from "react-router-dom";
import LoadingScreen from "components/LoadingScreen";
import Layout from "layouts/layout-v1/DashboardLayout";
import AuthGuard from "./page-sections/authentication/AuthGuard";
import ForgetPasswordVerifyCode from "./pages/authentication/forget-password-verify-code";

const Loadable = (Component: LazyExoticComponent<FC>) => (props: any) => {
  return (
    <Suspense fallback={<LoadingScreen />}>
      <Component {...props} />
    </Suspense>
  );
};

// authentication
const Login = Loadable(lazy(() => import("./pages/authentication/login")));
const Register = Loadable(lazy(() => import("./pages/authentication/register")));
const VerifyCode = Loadable(lazy(() => import("./pages/authentication/verify-code")));
const NewPassword = Loadable(lazy(() => import("./pages/authentication/new-password")));
const SwitchToCabinet = Loadable(lazy(() => import("./pages/authentication/switch-to-cabinet")));
const ForgetPassword = Loadable(lazy(() => import("./pages/authentication/forget-password")));
const TwoStepVerify = Loadable(lazy(() => import("./pages/authentication/two-step-verification")));

// dashboard home
const LearningManagement = Loadable(lazy(() => import("./pages/dashboards/learning-management")));

// account
const Account = Loadable(lazy(() => import("./pages/accounts/account")));

// reports
const PlanReport = Loadable(lazy(() => import("./pages/plan-report/details")));
const PlanReportList = Loadable(lazy(() => import("./pages/plan-report/list")));
const PivotPlanReport = Loadable(lazy(() => import("./pages/pivot-plan-report")));
const WorkReport = Loadable(lazy(() => import("./pages/work-report/details")));
const WorkReportList = Loadable(lazy(() => import("./pages/work-report/list")));
const IncomeReport = Loadable(lazy(() => import("./pages/report-income/details")));
const IncomeReportList = Loadable(lazy(() => import("./pages/report-income/list")));

// offers / contracts
const OfferList = Loadable(lazy(() => import("./pages/offer/list")));
const OfferDetails = Loadable(lazy(() => import("./pages/offer/details")));
const ContractList = Loadable(lazy(() => import("./pages/contracts/list")));
const ContractDetails = Loadable(lazy(() => import("./pages/contracts/details")));

// lessons
const LessonList = Loadable(lazy(() => import("./pages/lesson/list")));
const LessonDetails = Loadable(lazy(() => import("./pages/lesson/details")));

// docs
const DocsList = Loadable(lazy(() => import("./pages/docs/list")));

// schedule
const Schedule = Loadable(lazy(() => import("./pages/schedule")));

// calendly (public self-scheduling)
const CalendlyDashboard = Loadable(lazy(() => import("./pages/calendly/index")));
const CalendlyEventTypes = Loadable(lazy(() => import("./pages/calendly/event-types")));
const CalendlyAvailability = Loadable(lazy(() => import("./pages/calendly/availability")));
const CalendlyBookings = Loadable(lazy(() => import("./pages/calendly/bookings")));
const AdminCalendlyBookings = Loadable(lazy(() => import("./pages/admin/calendly-bookings")));

// ask question
const AskQuestion = Loadable(lazy(() => import("./pages/ask-question")));

// 404
const Error = Loadable(lazy(() => import("./pages/404")));

const authRoutes = [
  { path: "/", element: <Navigate to="/dashboard" /> },
  { path: "login", element: <Login /> },
  { path: "register", element: <Register /> },
  { path: "verify-code", element: <VerifyCode /> },
  { path: "forget-password-verify-code", element: <ForgetPasswordVerifyCode /> },
  { path: "new-password", element: <NewPassword /> },
  { path: "forget-password", element: <ForgetPassword /> },
  { path: "two-step-verification", element: <TwoStepVerify /> },
  { path: "switch-to-cabinet", element: <SwitchToCabinet /> },
];

const dashboardRoutes = [
  { path: "", element: <LearningManagement /> },

  { path: "account", element: <Account /> },

  { path: "offer-list", element: <OfferList /> },
  { path: "offer-details/:id", element: <OfferDetails /> },

  { path: "contract-list", element: <ContractList /> },
  { path: "contract-details/:id", element: <ContractDetails /> },

  { path: "work-report", element: <WorkReportList /> },
  { path: "work-report/:id", element: <WorkReport /> },

  { path: "income-report", element: <IncomeReportList /> },
  { path: "income-report/:id", element: <IncomeReport /> },

  { path: "plan-report", element: <PlanReportList /> },
  { path: "plan-report/:id", element: <PlanReport /> },
  { path: "pivot-plan-report", element: <PivotPlanReport /> },

  { path: "docs", element: <DocsList /> },

  { path: "lesson-list", element: <LessonList /> },
  { path: "lesson-details/:id", element: <LessonDetails /> },

  { path: "schedule", element: <Schedule /> },

  { path: "calendly", element: <CalendlyDashboard /> },
  { path: "calendly/event-types", element: <CalendlyEventTypes /> },
  { path: "calendly/availability", element: <CalendlyAvailability /> },
  { path: "calendly/bookings", element: <CalendlyBookings /> },
  { path: "admin/calendly/bookings", element: <AdminCalendlyBookings /> },

  { path: "ask-question", element: <AskQuestion /> },
];

const routes = () => {
  return [
    ...authRoutes,
    {
      path: "dashboard",
      element: (
        <AuthGuard>
          <Layout />
        </AuthGuard>
      ),
      children: dashboardRoutes,
    },
    {
      path: "*",
      element: <Error />,
    },
  ];
};

export default routes;
